// autopilot-answer-directive-command.ts
//
// Lệnh `avatar autopilot answer` — tách khỏi
// `autopilot-control-directive-commands.ts` (M1, code-reviewer 260916
// phase-04) để cả hai file dưới trần 200 dòng.
//
// Hợp đồng DUY NHẤT: `--question <id> --text <giá trị>` trả lời câu hỏi
// interview (B4) — `interview-answer-directive-processing.ts` ghi vào
// interview.json, gỡ chặn dispatch. H17 (audit 2026-09-28): dạng CŨ
// `<ticket> --request <id>` từng in receipt `queued` + exit 0 trong khi
// controller LUÔN từ chối payload đó ⇒ nay từ chối ngay tại CLI, exit 1,
// không ghi directive. `[ticket]`/`--request` vẫn khai để người gõ dạng cũ
// nhận hướng dẫn thay vì lỗi "unknown option" của commander.
import type { Command } from "commander";
import { writeMachineReadableJson } from "../machine-readable-json-output-contract.js";
import { requireFlag } from "../non-interactive-context.js";
import { log } from "../terminal-logger.js";
import {
  type ControlDirectiveDeps,
  writeControlDirective,
} from "./autopilot-control-directive-shared.js";

export function registerAutopilotAnswerDirectiveCommand(
  autopilot: Command,
  deps: ControlDirectiveDeps,
): void {
  autopilot
    .command("answer [ticket]")
    .description("Trả lời câu hỏi interview (--question <id> --text <...>)")
    .option("--mission <id>", "Mission ID")
    .option("--question <id>", "Question ID của câu hỏi interview (B4)")
    .option("--request <id>", "ĐÃ BỎ — dùng --question")
    .option("--text <text>", "Nội dung trả lời")
    .option("--repo <path>", "Repo path để resolve mission")
    .option("--json", "Output JSON")
    .action(
      async (
        _ticket: string | undefined,
        opts: {
          mission?: string;
          question?: string;
          request?: string;
          text?: string;
          repo?: string;
          json?: boolean;
        },
      ) => {
        // Legacy form, alone or next to `--question` (was dropped silently).
        if (opts.request !== undefined) {
          rejectLegacyAnswerForm(!!opts.json);
          return;
        }
        // No legacy flag given ⇒ a plain omission: name the required flag.
        const questionId = requireFlag(opts.question, {
          flag: "--question",
          command: "autopilot answer",
          hint: "--question <question-id> (vd T-0007-needs-decision-1a2b3c4d; `avatar autopilot status --decisions` liệt kê câu đang chờ kèm id)",
        });
        const text = requireFlag(opts.text, {
          flag: "--text",
          command: "autopilot answer",
          hint: "--text <option id, vd node-test — xem `avatar autopilot status --decisions`; nhãn duy nhất cũng được; câu text gõ tự do>",
        });
        const workspaceRoot = await deps.resolveWorkspaceRoot(opts.repo);

        await writeControlDirective(
          deps,
          workspaceRoot,
          opts.mission,
          "answer",
          { question_id: questionId, value: text },
          !!opts.json,
        );
      },
    );
}

export const LEGACY_ANSWER_MESSAGE =
  "`autopilot answer <ticket> --request <id>` đã bỏ — controller chỉ nhận câu trả lời interview. " +
  "Dùng: avatar autopilot answer --question <question-id> --text <...> " +
  "(câu hỏi needs_decision có id `<ticket>-needs-decision-<8 hex>`; giá trị = option id, vd `node-test` — " +
  "`avatar autopilot status --decisions` liệt kê câu đang chờ kèm option id + nhãn; nhãn duy nhất cũng được nhận, câu `text` thì gõ tự do).";

function rejectLegacyAnswerForm(jsonOut: boolean): void {
  if (jsonOut) {
    writeMachineReadableJson({
      command: "autopilot answer",
      ok: false,
      reason: "legacy_request_form_removed",
      message: LEGACY_ANSWER_MESSAGE,
    });
  } else {
    log.error(LEGACY_ANSWER_MESSAGE);
  }
  process.exit(1);
}
